class NativeCarouselControls extends HTMLElement {
  connectedCallback() {
    if (this.events) return;
    this.track = this.querySelector('[data-native-track]');
    this.controls = this.querySelector('[data-native-controls]');
    this.previous = this.querySelector('[data-native-previous]');
    this.next = this.querySelector('[data-native-next]');
    this.progress = this.querySelector('[data-native-progress]');
    if (!this.track || !this.controls || !this.previous || !this.next || !this.progress) return;
    this.events = new AbortController();
    const options = {signal: this.events.signal};
    this.previous.addEventListener('click', () => this.move(-1), options);
    this.next.addEventListener('click', () => this.move(1), options);
    this.track.addEventListener('scroll', () => this.update(), {...options, passive: true});
    this.track.addEventListener('keydown', event => {
      if (event.target !== this.track || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      if (event.key === 'Home') this.scrollToPosition(0);
      else if (event.key === 'End') this.scrollToPosition(this.maximum);
      else this.move((event.key === 'ArrowRight' ? 1 : -1) * this.direction);
    }, options);
    this.progress.addEventListener('click', event => {
      const bounds = this.progress.getBoundingClientRect();
      if (this.maximum <= 1 || !Number.isFinite(bounds.width) || bounds.width <= 0) return;
      event.preventDefault();
      const ratio = Math.max(0, Math.min(1, (event.clientX - bounds.left) / bounds.width));
      this.scrollToPosition(this.maximum * (this.direction === 1 ? ratio : 1 - ratio));
    }, options);
    this.observer = new ResizeObserver(() => this.update());
    this.observer.observe(this.track);
    for (const item of this.track.querySelectorAll('[data-native-item]')) this.observer.observe(item);
    this.update();
  }

  disconnectedCallback() {
    this.events?.abort();
    this.events = null;
    this.observer?.disconnect();
  }

  get direction() { return getComputedStyle(this.track).direction === 'rtl' ? -1 : 1; }
  get maximum() { return Math.max(0, this.track.scrollWidth - this.track.clientWidth); }
  get position() { return Math.max(0, Math.min(this.maximum, this.track.scrollLeft * this.direction)); }

  update() {
    const maximum = this.maximum;
    const position = this.position;
    const overflow = maximum > 1;
    this.controls.hidden = this.progress.hidden = !overflow;
    this.toggleAttribute('data-native-overflow', overflow);
    this.previous.disabled = position <= 1;
    this.next.disabled = position >= maximum - 1;
    this.progress.style.setProperty('--progress-ratio', maximum ? position / maximum : 0);
    this.progress.style.setProperty('--visible-ratio', this.track.scrollWidth ? Math.min(1, this.track.clientWidth / this.track.scrollWidth) : 1);
  }

  scrollToPosition(position) {
    this.track.scrollTo({
      left: this.direction * Math.max(0, Math.min(this.maximum, position)),
      behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth',
    });
  }

  move(direction) {
    const items = this.track.querySelectorAll('[data-native-item]');
    const first = items[0]?.getBoundingClientRect();
    const second = items[1]?.getBoundingClientRect();
    const step = second ? Math.abs(second.left - first.left) : first?.width || this.track.clientWidth;
    this.scrollToPosition(this.position + direction * step);
  }
}

if (!customElements.get('native-carousel-controls')) {
  customElements.define('native-carousel-controls', NativeCarouselControls);
}
